import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lightbulb } from "@phosphor-icons/react";
import { toast } from "sonner";
import GoogleAuthButton from "@/components/GoogleAuthButton";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      navigate(`/app/${u.role === "agency_admin" ? "agency" : u.role}`);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-zinc-50 bg-grid flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-9 h-9 bg-yellow-400 rounded-lg flex items-center justify-center">
            <Lightbulb weight="fill" size={20} className="text-black" />
          </div>
          <span className="font-heading font-black text-xl">Mastra Media</span>
        </Link>

        <div className="backdrop-blur-xl bg-zinc-900/70 border border-white/10 rounded-2xl p-8">
          <h1 className="font-heading font-black text-3xl mb-2">Sign in</h1>
          <p className="text-zinc-400 text-sm mb-6">Continue to your workspace</p>

          <GoogleAuthButton testid="login-google-button" />

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-[#0a0a0a] px-3 text-zinc-500 font-mono uppercase tracking-wider">or</span>
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-black border-white/10 mt-1.5 h-11"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-zinc-300">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-black border-white/10 mt-1.5 h-11"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-rose-400 text-sm bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-11 rounded-lg"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="text-sm text-zinc-400 text-center mt-6">
            Don't have an account?{" "}
            <Link to="/register" data-testid="login-register-link" className="text-yellow-400 hover:text-yellow-300 font-medium">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
